import React from 'react'
import {useLocation} from 'wouter'
import {create} from 'zustand'
import {produce} from 'immer'
import {followPath} from '../lib/geo'
import {lineString} from '@turf/turf'
import {
  default as Map,
  GeolocateControl,
  LngLatBoundsLike,
  MapMouseEvent,
  MapRef,
  Marker,
  NavigationControl,
  Popup,
  ScaleControl,
  Source,
  Layer,
} from 'react-map-gl/mapbox'
import type {FeatureCollection} from 'geojson'
import {Trip} from '../lib/model'
import {ILatLng, IMapable} from '@fotolog/data'
import {toast, tripBounds, useSession} from '../lib'
import {Avatar, Box, Divider, IconButton} from '@mui/material'
import {
  IconClickMap,
  IconCopy,
  IconFlyover,
  IconGrabMap,
  IconMarker,
  IconNavHome,
  IconNavNext,
  IconNavPrev,
  IconSatellite,
  IconTerrain,
} from '../Icons'
import 'mapbox-gl/dist/mapbox-gl.css'

function getBounds() {
  const json = localStorage.getItem('trip.bounds') || '[{"lng":-230,"lat":-54},{"lng":124,"lat":77}]'
  if (json) {
    return JSON.parse(json) as LngLatBoundsLike
  }
  return undefined
}

// MapState is used to manage FotoLog specific functionality so that EpicMap & TripMap can control ReactMap accordingly.
type MapStyle = 'terrain' | 'satellite'
type MapMode = 'navigate' | 'click' | 'flyover'
const STYLES = {
  satellite: 'mapbox://styles/fotolog/clbs757a9000515nyh75lnbut',
  terrain: 'mapbox://styles/fotolog/clbprqhym000514p9m5owjxgm',
}
interface IPopupable {
  lat: number
  lng: number
}
export interface MapState {
  style: MapStyle
  mode: MapMode
  setStyle: (style: MapStyle) => void
  setMode: (mode: MapMode) => void
  marker?: IMapable
  openMarker: (marker: IMapable) => void
  closeMarker: () => void
  popup?: IPopupable
  openPopup: (popup: IPopupable) => void
  closePopup: () => void
}
type MapStateSetter = (x: Partial<MapState> | ((x: MapState) => MapState)) => MapState
const mapStore = (set: MapStateSetter, get: () => MapState) => ({
  style: 'terrain',
  mode: 'navigate',
  setStyle: (style: MapStyle) => {
    set({style})
  },
  setMode: (mode: 'navigate' | 'click') => {
    set({mode})
  },
  openMarker: (marker: IMapable) => {
    // click counter is needed so user can bring up detail panel if marker is the current one
    if (!marker.clicked) {
      marker.clicked = 0
    }
    marker.clicked += 1
    set({marker})
  },
  closeMarker: () => {
    set({marker: undefined})
  },
  openPopup: (popup: IPopupable) => {
    set({popup})
  },
  closePopup: () => {
    set({popup: undefined})
  },
})
export const useMapState = create<MapState>(produce(mapStore))

function FotologControl({trip}: {trip?: Trip}) {
  const map = useMapState()
  const IconStyle = map.style !== 'satellite' ? IconSatellite : IconTerrain
  const IconMode = map.mode !== 'click' ? IconClickMap : IconGrabMap
  const links = []
  if (trip) {
    if (trip.priorId) {
      links.push(<Divider key="divider-prior" />)
      links.push(
        <IconButton
          key="trip-prior"
          href={`/trip/${trip.priorId}`}
          color="secondary"
          title="Go to Prior Trip"
          sx={{p: '8px 5px'}}
        >
          <IconNavPrev fontSize="small" />
        </IconButton>
      )
    }
    if (trip.parentId) {
      links.push(<Divider key="divider-parent" />)
      links.push(
        <IconButton
          key="trip-parent"
          href={`/trip/${trip.parentId}`}
          color="secondary"
          title="Go to Parent Trip"
          sx={{p: '8px 5px'}}
        >
          <IconNavHome fontSize="small" />
        </IconButton>
      )
    }
    if (trip.nextId) {
      links.push(<Divider key="divider-next" />)
      links.push(
        <IconButton
          key="trip-next"
          href={`/trip/${trip.nextId}`}
          color="secondary"
          title="Go to Next Trip"
          sx={{p: '8px 5px'}}
        >
          <IconNavNext fontSize="small" />
        </IconButton>
      )
    }
    if (trip.route.length) {
      links.push(<Divider key="divider-next" />)
      links.push(
        <IconButton
          onClick={() => map.setMode('flyover')}
          color="primary"
          aria-label="Fly Over Route"
          title="Fly Over Route"
          sx={{padding: '8px 5px'}}
        >
          <IconFlyover fontSize="small" />
        </IconButton>
      )
    }
  }
  return (
    <Box position="absolute" top={150} right={8} border="1px solid #999" p={0} bgcolor="white" borderRadius={2}>
      <IconButton
        onClick={() => map.setStyle(map.style !== 'satellite' ? 'satellite' : 'terrain')}
        color="primary"
        aria-label={map.style !== 'satellite' ? 'Satellite View' : 'Terrain View'}
        title={map.style !== 'satellite' ? 'Satellite View' : 'Terrain View'}
        sx={{padding: '8px 5px'}}
      >
        <IconStyle fontSize="small" />
      </IconButton>
      <Divider />
      <IconButton
        onClick={() => map.setMode(map.mode === 'click' ? 'navigate' : 'click')}
        color="primary"
        aria-label={map.mode !== 'click' ? 'Click Mode' : 'Grab Mode'}
        title={map.mode !== 'click' ? 'Click Mode' : 'Grab Mode'}
        sx={{padding: '8px 5px'}}
      >
        <IconMode fontSize="small" />
      </IconButton>
      {links.map(i => i)}
    </Box>
  )
}

type EpicMapProps = {
  trips: Trip[]
  width: number
  size?: '100%' | number
  onBoundsChange?: (bl: ILatLng, ur: ILatLng) => void
}
export function EpicMap({trips, width, size = '100%', onBoundsChange}: EpicMapProps) {
  const session = useSession()
  const setLocation = useLocation()[1]
  const map = useMapState()
  const mapRef = React.useRef<MapRef>()
  React.useEffect(() => {
    mapRef.current?.resize()
  }, [width])
  React.useEffect(() => {
    if (mapRef.current && map.marker) {
      const center = map.marker.gps
      setLocation(`/globe/trip/${map.marker.id}`, {replace: true})
      // give detail panel a chance to render first
      setTimeout(() => mapRef?.current?.flyTo({center}), 50)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.marker?.id, map.marker?.clicked])

  const onMove = () => {
      const bounds = mapRef?.current?.getBounds()
    if (onBoundsChange && bounds) {
      onBoundsChange(bounds.getSouthWest() as ILatLng, bounds.getNorthEast() as ILatLng)
    }
  }

  const mapable = trips
    .filter(i => i.cover?.gps)
    .map(i => {
      return {
        trip: i,
        foto: i.cover,
        id: i.id,
        title: i.title,
        img: i.cover?.thumbnail,
        gps: i.cover?.gps,
        marker: true,
        clicked: 0,
      } as IMapable
    })

  return (
    <Box position="absolute" top={0} left={0} bottom={0} width={width}>
      <Map
        id="all-trips"
        initialViewState={{
          bounds: getBounds(),
        }}
        mapStyle={STYLES[map.style]}
        projection={{name: 'globe'}}
        ref={mapRef as React.RefObject<MapRef>}
        onMoveEnd={onMove}
        mapboxAccessToken={session.keys.mapbox}
        style={{width: width, height: '100%'}}
        cursor={map.mode === 'click' ? 'pointer' : 'grab'}
      >
        <NavigationControl position="top-right" />
        <ScaleControl position="bottom-right" />
        <FotologControl />

        {mapable.map(pt => (
          <Marker
            key={pt.id}
            latitude={pt.gps.lat}
            longitude={pt.gps.lng}
            onClick={e => {
              e.originalEvent.stopPropagation()
              map.openMarker(pt)
            }}
          >
            {pt.img ? (
              <Avatar
                alt={pt.title}
                src={pt.img}
                sx={{
                  width: map.marker?.id === pt.id ? 60 : 30,
                  height: map.marker?.id === pt.id ? 60 : 30,
                  boxShadow: `2px 2px 3px #333`,
                }}
                className="app-clickable"
              />
            ) : (
              <IconMarker
                className="app-clickable"
                color={map.marker?.id === pt.id ? 'secondary' : 'primary'}
                fontSize={map.marker?.id === pt.id ? 'large' : 'medium'}
              />
            )}
          </Marker>
        ))}
      </Map>
    </Box>
  )
}
export type SizeStyle = {
  width: number | '100%'
  height: number | '100%'
}

type TripMapProps = {
  trip: Trip
  width: number
  readonly?: boolean
  openImg?: (evt: React.SyntheticEvent, idx: number) => void
}
export function TripMap({trip, width, readonly = false, openImg}: TripMapProps) {
  const session = useSession()
  const setLocation = useLocation()[1]
  const map = useMapState()
  const mapRef = React.useRef<MapRef>()
  React.useEffect(() => {
    mapRef.current?.resize()
  }, [width])
  React.useEffect(() => {
    if (mapRef.current && map.marker) {
      // don't want info or foto navigation to push new entries to browser history
      setLocation(`/trip/${trip.id}-${trip.slug}/${map.marker.id}`, {replace: true})
      const center = map.marker.gps
      // give detail panel a chance to render first
      setTimeout(() => mapRef.current?.flyTo({center}), 50)
    }
  }, [map.marker?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // for route mapping & animation
  const [pathState, setPathState] = React.useState({phase: 0})
  React.useEffect(() => {
    if (mapRef.current && map.mode === 'flyover') {
      followPath(mapRef.current, trip.route, {
        rotate: false,
        update: state => {
          setPathState(state)
          return null
        },
      })
      map.setMode('navigate')
    }
  }, [map.mode]) // eslint-disable-line react-hooks/exhaustive-deps
  const route: FeatureCollection = {
    type: 'FeatureCollection',
    features: trip.route.length ? [lineString(trip.route)] : [],
  }

  if (trip.mapables.length === 0) {
    return null
  }

  const onClick = (evt: MapMouseEvent) => {
    if (mapRef.current && map.mode === 'click' && !map.popup) {
      map.openPopup(evt.lngLat)
    }
  }

  const onGeoLocated = (data: GeolocationPosition) => {
    console.log('GEO POS:', data.coords)
  }

  return (
    <Box position="absolute" top={0} left={0} bottom={0} width={width}>
      <Map
        key={trip.id}
        id={`trip-${trip.id}`}
        initialViewState={{
          bounds: tripBounds(trip.mapables),
        }}
        ref={mapRef as React.RefObject<MapRef>}
        mapStyle={STYLES[map.style]}
        mapboxAccessToken={session.keys.mapbox}
        style={{width: width, height: '100%'}}
        cursor={map.mode === 'click' ? 'pointer' : 'grab'}
        onClick={onClick}
      >
        {route && (
          <Source id="route-line" type="geojson" data={route} lineMetrics={true}>
            <Layer
              id="route-line"
              type="line"
              paint={{
                'line-gradient': ['step', ['line-progress'], 'red', pathState.phase, 'rgba(255, 0, 0, 0.33)'],
                'line-width': 2,
              }}
            />
          </Source>
        )}

        <GeolocateControl
          position="top-right"
          trackUserLocation={true}
          positionOptions={{enableHighAccuracy: true}}
          onGeolocate={onGeoLocated}
        />
        <NavigationControl position="top-right" />
        <ScaleControl position="bottom-right" />
        <FotologControl trip={trip} />
        {map.popup && (
          <Popup latitude={map.popup.lat} longitude={map.popup.lng} maxWidth="400" onClose={() => map.closePopup()}>
            <Box className="app-flex">
              <dl className="app-keyval">
                <dt>Latitude:</dt>
                <dd>{map.popup.lat}</dd>
                <dt>Longitude:</dt>
                <dd>{map.popup.lng}</dd>
              </dl>
              <IconButton
                color="primary"
                title="Copy Coordinates"
                onClick={() => {
                  navigator.clipboard.writeText(`${map.popup?.lat}, ${map.popup?.lng}`)
                  toast('info', 'Coordinates Copied to Clipboard')
                }}
              >
                <IconCopy />
              </IconButton>
            </Box>
          </Popup>
        )}
        {trip.mapables.map(pt => {
          return (
            <Marker
              key={pt.id}
              latitude={pt.gps.lat}
              longitude={pt.gps.lng}
              onClick={e => {
                e.originalEvent.stopPropagation()
                map.openMarker(pt.id !== map.marker?.id ? pt : map.marker)
              }}
            >
              <Avatar
                alt={pt.title}
                src={pt.img}
                sx={{
                  width: map.marker?.id === pt.id ? 60 : 30,
                  height: map.marker?.id === pt.id ? 60 : 30,
                  boxShadow: `2px 2px 3px #333`,
                }}
                className="app-clickable"
              />
            </Marker>
          )
        })}
      </Map>
    </Box>
  )
}
